/**
 * Two-Factor Authentication Service (frontend)
 * =============================================
 * Thin wrappers around the /api/auth/2fa/* endpoints.
 *
 * The backend returns the `otpauth://` URI only; the QR is rendered on the
 * client (see TwoFactorEnrollment.tsx) so the secret never travels as a
 * pre-rendered image.
 */

import axios from 'axios';
import { apiClient } from './api';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

function extractError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message as string | undefined;
    return msg ?? fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export interface TwoFactorStatus {
  enabled: boolean;
  required: boolean;
  graceDaysRemaining: number | null;
  backupCodesRemaining: number;
}

export async function getTwoFactorStatus(): Promise<TwoFactorStatus> {
  try {
    const res = await apiClient.get<ApiResponse<TwoFactorStatus>>('/api/auth/2fa/status');
    return res.data.data;
  } catch (err) {
    throw new Error(extractError(err, 'Failed to load 2FA status.'));
  }
}

export interface EnrollmentSecrets {
  otpauthUri: string;
  /** Base32 secret — shown once alongside the QR for manual entry. */
  secret: string;
}

export async function startEnrollment(): Promise<EnrollmentSecrets> {
  try {
    const res = await apiClient.post<ApiResponse<EnrollmentSecrets>>('/api/auth/2fa/enroll');
    return res.data.data;
  } catch (err) {
    throw new Error(extractError(err, 'Could not start 2FA enrolment.'));
  }
}

export async function activateTwoFactor(code: string): Promise<{ backupCodes: string[] }> {
  try {
    const res = await apiClient.post<ApiResponse<{ backupCodes: string[] }>>(
      '/api/auth/2fa/activate',
      { code },
    );
    return res.data.data;
  } catch (err) {
    throw new Error(extractError(err, 'That code is not valid. Try again.'));
  }
}

export async function disableTwoFactor(currentPassword: string, code: string): Promise<void> {
  try {
    await apiClient.post<ApiResponse<null>>('/api/auth/2fa/disable', {
      currentPassword,
      code,
    });
  } catch (err) {
    throw new Error(extractError(err, 'Could not disable 2FA.'));
  }
}

export async function regenerateBackupCodes(): Promise<string[]> {
  try {
    const res = await apiClient.post<ApiResponse<{ backupCodes: string[] }>>(
      '/api/auth/2fa/backup-codes/regenerate',
    );
    return res.data.data.backupCodes;
  } catch (err) {
    throw new Error(extractError(err, 'Could not regenerate backup codes.'));
  }
}

/** HR/IT admin: wipe 2FA for a user so they can re-enrol. */
export async function resetTwoFactorForUser(userId: string): Promise<void> {
  try {
    await apiClient.post<ApiResponse<null>>(`/api/auth/users/${userId}/2fa/reset`);
  } catch (err) {
    throw new Error(extractError(err, 'Could not reset 2FA for that user.'));
  }
}
